import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Trash2, Mail, MessageSquare } from 'lucide-react';
import { MergeFieldTextInput } from './MergeFieldTextInput';

const NONE = '_none';

export const SuccessCoachingWeekEditor = ({ week, mergeFields, messageMergeFields, onChange, readOnly }) => {
  const n = week.week_number;
  const topicField = mergeFields.find(f => f.name === week.topic_field);
  const topicLabel = topicField ? (topicField.static_value || topicField.display_name) : week.topic_label;
  const setQuestion = (i, v) => onChange({ ...week, questions: week.questions.map((q, idx) => idx === i ? v : q) });
  const removeQuestion = (i) => onChange({ ...week, questions: week.questions.filter((_, idx) => idx !== i) });
  const answered = week.questions.filter(q => q.trim()).length;

  return (
    <Card className="card-shadow" data-testid={`sc-week-${n}`}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2 flex-wrap">
          Week {n}
          {topicLabel && <span className="text-sm font-normal text-muted-foreground" data-testid={`sc-week-${n}-topic-label`}>— {topicLabel}</span>}
          <Badge variant="outline" className="ml-auto text-[10px]">{answered} question{answered === 1 ? '' : 's'}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1">
          <Label className="text-xs">Topic (custom merge field)</Label>
          <Select value={week.topic_field || NONE} onValueChange={(v) => onChange({ ...week, topic_field: v === NONE ? '' : v })} disabled={readOnly}>
            <SelectTrigger data-testid={`sc-week-${n}-topic-select`}><SelectValue placeholder="Select a topic" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>No topic set</SelectItem>
              {week.topic_field && !topicField && <SelectItem value={week.topic_field}>{`{{custom_${week.topic_field}}}`}</SelectItem>}
              {mergeFields.map(f => (
                <SelectItem key={f.name} value={f.name}>{`{{custom_${f.name}}}`} · {f.display_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-xs">Questions the Success Coach asks</Label>
          {week.questions.map((q, i) => (
            <div key={i} className="flex gap-2">
              <span className="text-xs text-muted-foreground w-5 pt-2.5">{i + 1}.</span>
              <Input value={q} onChange={(e) => setQuestion(i, e.target.value)} disabled={readOnly} data-testid={`sc-week-${n}-question-${i}`} />
              {!readOnly && (
                <Button variant="ghost" size="icon" onClick={() => removeQuestion(i)} data-testid={`sc-week-${n}-remove-question-${i}`}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              )}
            </div>
          ))}
          {!readOnly && (
            <Button variant="outline" size="sm" onClick={() => onChange({ ...week, questions: [...week.questions, ''] })} data-testid={`sc-week-${n}-add-question`}>
              <Plus className="w-3 h-3 mr-1" />Add Question
            </Button>
          )}
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2 rounded-md border p-3">
            <p className="text-sm font-medium flex items-center gap-1"><Mail className="w-4 h-4" />Email to participant</p>
            <MergeFieldTextInput label="Subject" value={week.email_subject} mergeFields={messageMergeFields} disabled={readOnly}
              onChange={(v) => onChange({ ...week, email_subject: v })} testId={`sc-week-${n}-email-subject`} />
            <MergeFieldTextInput label="Body" multiline rows={6} value={week.email_body} mergeFields={messageMergeFields} disabled={readOnly}
              onChange={(v) => onChange({ ...week, email_body: v })} testId={`sc-week-${n}-email-body`}
              placeholder="Leave blank to not send an email this week" />
          </div>
          <div className="space-y-2 rounded-md border p-3">
            <p className="text-sm font-medium flex items-center gap-1"><MessageSquare className="w-4 h-4" />Text (SMS) to participant</p>
            <MergeFieldTextInput label="Message" multiline rows={6} value={week.sms_body} mergeFields={messageMergeFields} disabled={readOnly}
              onChange={(v) => onChange({ ...week, sms_body: v })} testId={`sc-week-${n}-sms-body`}
              placeholder="Leave blank to not send a text this week"
              footer={<p className="text-[11px] text-muted-foreground" data-testid={`sc-week-${n}-sms-length`}>{(week.sms_body || '').length} characters (before merge fields are filled in)</p>} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
